/**
 * Содержимое выдвижной панели «Стены». Две вкладки, чтобы рисование не тонуло в настройках:
 *
 *   • «Рисовать» — что и как чертить: тип стены, способ (цепочка, одна стена, прямоугольник,
 *     повёрнутый прямоугольник — как «Геометрический вариант» в ArchiCAD), комната по размеру
 *     («Создать комнату»: окошко с шириной и длиной, потом комната едет за курсором) и план
 *     квартиры для обводки (`UnderlayPanel`). Текущие параметры видны одной строкой-сводкой.
 *   • «Параметры» — толщина, высота и материал стен, которые будут нарисованы дальше.
 *
 * Выделенная стена — над вкладками: её правят прямо здесь, это не параметры будущих стен.
 */
import { useState, type ReactNode } from "react";
import { CATALOG, THICKNESS_PRESETS } from "@/shared/catalog";
import { formatArea, formatLengthNumber, formatMillimeters } from "@/shared/lib/geometry";
import { useLengthUnit } from "@/shared/store";
import { UIKit } from "@/shared/ui";
import { WallMaterialPicker } from "@/widgets/catalog";
import { SelectionKind, Tool, useEditorStore, type WallMethod } from "@/widgets/editor";
import { WallSizeFields } from "@/widgets/properties-panel";
import { CreateRoomDialog } from "@/widgets/draw-wall";
import { UnderlayAutoWallsSection, UnderlayPanel } from "@/widgets/underlay";

type WallsTab = "draw" | "settings";

const TABS: { value: WallsTab; label: string; title: string }[] = [
	{
		value: "draw",
		label: "Рисовать",
		title: "Тип стены, способ черчения, комната и план квартиры",
	},
	{ value: "settings", label: "Параметры", title: "Толщина, высота и материал новых стен" },
];

/** Способы черчения: иконка, короткое и полное название, как чертить */
const METHODS: {
	method: WallMethod;
	short: string;
	name: string;
	how: string;
	icon: ReactNode;
}[] = [
	{
		method: "chain",
		short: "Цепочка",
		name: "Цепочка",
		how: "стена за стеной. Правый клик - закончить, Ctrl+Z - убрать последнюю стену",
		icon: <UIKit.IconWallChain />,
	},
	{
		method: "single",
		short: "Одна",
		name: "Одна стена",
		how: "две точки - одна стена, следующая начинается заново",
		icon: <UIKit.IconWallSingle />,
	},
	{
		method: "rectangle",
		short: "Комната",
		name: "Прямоугольник",
		how: "два противоположных угла - комната; у готовой стены прилипает к ней",
		icon: <UIKit.IconWallRect />,
	},
	{
		method: "rotated",
		short: "Под углом",
		name: "Повёрнутый прямоугольник",
		how: "первая стена под любым углом, потом ширина комнаты",
		icon: <UIKit.IconWallRotated />,
	},
];

const SECTION_TITLE = "text-xs font-semibold tracking-label text-ink uppercase";

export const WallsDrawer = () => {
	const tool = useEditorStore((s) => s.tool);
	const setWallRole = useEditorStore((s) => s.setWallRole);
	const wallRole = useEditorStore((s) => s.wallRole);
	const wallMethod = useEditorStore((s) => s.wallMethod);
	const setWallMethod = useEditorStore((s) => s.setWallMethod);
	const currentMethod = METHODS.find((m) => m.method === wallMethod) ?? METHODS[0];
	const wallSettings = useEditorStore((s) => s.wallSettings);
	const setWallSettings = useEditorStore((s) => s.setWallSettings);
	const setTool = useEditorStore((s) => s.setTool);
	const roomSize = useEditorStore((s) => s.roomSize);
	const [creatingRoom, setCreatingRoom] = useState(false);
	const [tab, setTab] = useState<WallsTab>("draw");
	const selection = useEditorStore((s) => s.selection);
	const selectedWallId = selection?.kind === SelectionKind.Wall ? selection.id : null;
	const unit = useLengthUnit();
	const bearing = wallRole === "bearing";
	const materialName =
		CATALOG.wall.items.find((item) => item.material === wallSettings.material)?.name ?? "";

	return (
		<div className="contents">
			{/* Выделенная стена — сверху и прямо здесь: длину, толщину и высоту правят тут же, а не
			    ищут в правой панели. Вкладки ниже — только про стены, которые ещё нарисуют */}
			{selectedWallId && (
				<div className="flex flex-col gap-2 rounded-lg border border-accent/40 bg-accent-soft/40 p-2.5">
					<UIKit.Text as="span" className={SECTION_TITLE}>
						Выделенная стена
					</UIKit.Text>
					<WallSizeFields wallId={selectedWallId} />
				</div>
			)}

			<UIKit.SegmentedControl<WallsTab>
				className="w-full"
				value={tab}
				options={TABS}
				onChange={setTab}
			/>

			{tab === "draw" ? (
				<>
					{/* Тип стены: две кнопки рядом — видно, что это выбор одного из двух */}
					<div className="flex flex-col gap-1.5">
						<div className="grid grid-cols-2 gap-1.5">
							<UIKit.Button
								type="button"
								title="Перегородка (W). Tab — переключить тип во время черчения"
								className={`flex items-center justify-center gap-1.5 rounded-lg border p-2 text-sm${
									tool === Tool.Wall && !bearing
										? " border-accent bg-accent-soft font-medium text-accent"
										: " border-border bg-surface text-ink hover:border-accent/50 hover:bg-bg"
								}`}
								onClick={(event) => {
									event.currentTarget.blur();
									setWallRole("partition");
								}}
							>
								<UIKit.IconWall />
								Перегородка
							</UIKit.Button>
							<UIKit.Button
								type="button"
								title="Несущая стена. Tab — переключить тип во время черчения"
								className={`flex items-center justify-center gap-1.5 rounded-lg border p-2 text-sm${
									tool === Tool.Wall && bearing
										? " border-[#d9958f] bg-[#fdf1f0] font-medium text-red-700 dark:border-red-500/50 dark:bg-red-950/30 dark:text-red-300"
										: " border-border bg-surface text-ink hover:border-red-300 hover:bg-red-50/50 dark:hover:border-red-500/50 dark:hover:bg-red-950/20"
								}`}
								onClick={(event) => {
									event.currentTarget.blur();
									setWallRole("bearing");
								}}
							>
								<UIKit.IconWall />
								Несущая
							</UIKit.Button>
						</div>
						{/* Чем будем рисовать — одной строкой; клик ведёт в «Параметры» */}
						<UIKit.Button
							type="button"
							title="Изменить толщину, высоту и материал новых стен"
							className="flex items-center gap-2 rounded-lg bg-bg px-2.5 py-1.5 text-left text-xs text-ink hover:bg-accent-soft"
							onClick={() => setTab("settings")}
						>
							<UIKit.Text as="span" className="min-w-0 flex-1 truncate tabular-nums">
								{formatMillimeters(wallSettings.thickness, unit)} · высота{" "}
								{formatMillimeters(wallSettings.height, unit)} · {materialName}
							</UIKit.Text>
							<UIKit.Text as="span" className="shrink-0 font-medium text-accent">
								Изменить
							</UIKit.Text>
						</UIKit.Button>
					</div>

					{/* Способ черчения: как в ArchiCAD — цепочка, одна стена, два вида прямоугольника */}
					<div className="flex flex-col gap-1.5">
						<UIKit.Text as="span" className={SECTION_TITLE}>
							Способ
						</UIKit.Text>
						<div className="grid grid-cols-4 gap-1" role="group" aria-label="Способ черчения стен">
							{METHODS.map(({ method, short, name, how, icon }) => (
								<UIKit.Button
									key={method}
									type="button"
									title={`${name}. ${how}`}
									aria-label={name}
									aria-pressed={wallMethod === method}
									className={`flex flex-col items-center justify-center gap-0.5 rounded-md border px-0.5 py-1.5 text-xs${
										wallMethod === method
											? " border-accent bg-accent-soft font-medium text-accent"
											: " border-border bg-surface text-ink hover:border-accent/50 hover:bg-bg"
									}`}
									onClick={(event) => {
										event.currentTarget.blur();
										setWallMethod(method);
									}}
								>
									{icon}
									<UIKit.Text as="span" className="truncate text-xs">
										{short}
									</UIKit.Text>
								</UIKit.Button>
							))}
						</div>
						<UIKit.Text className="text-xs text-ink">
							<UIKit.Text as="span" className="font-medium">
								{currentMethod.name}:
							</UIKit.Text>{" "}
							{currentMethod.how}
						</UIKit.Text>
					</div>

					{/* Комната по размеру: окошко с шириной и длиной, потом комната едет за курсором */}
					<UIKit.Button
						type="button"
						title="Задать ширину и длину — комната поедет за курсором, клик поставит её"
						className={`flex items-center justify-center gap-2 rounded-lg border px-2.5 py-2.25 text-sm${
							tool === Tool.Room
								? " border-accent bg-accent-soft font-medium text-accent"
								: " border-border bg-surface text-ink hover:border-accent/50 hover:bg-bg"
						}`}
						onClick={(event) => {
							event.currentTarget.blur();
							if (tool === Tool.Room) setTool(Tool.Select);
							else setCreatingRoom(true);
						}}
					>
						<UIKit.IconArea />
						{tool === Tool.Room
							? `Отменить комнату ${formatArea(roomSize.width * roomSize.depth)}`
							: "Создать комнату по размеру"}
					</UIKit.Button>
					{creatingRoom && <CreateRoomDialog onClose={() => setCreatingRoom(false)} />}

					{/* План БТИ для обводки и автораспознавание стен по нему */}
					<UnderlayPanel />
					<UnderlayAutoWallsSection />
				</>
			) : (
				<>
					<div className="flex flex-col gap-2">
						<UIKit.Text as="span" className={SECTION_TITLE}>
							{bearing ? "Новые несущие стены" : "Новые перегородки"}
						</UIKit.Text>
						<UIKit.Text className="text-xs text-ink">
							Для стен, которые нарисуете дальше. Нарисованную стену правят, выделив её.
						</UIKit.Text>
					</div>

					<div className="flex flex-col gap-2">
						<UIKit.Text as="span" className={SECTION_TITLE}>
							Размеры
						</UIKit.Text>
						<div className="grid grid-cols-4 gap-1">
							{THICKNESS_PRESETS.map((value) => (
								<UIKit.Button
									key={value}
									type="button"
									className={`rounded-md border border-border bg-surface py-1.5 text-xs text-ink tabular-nums hover:border-accent/50 hover:text-ink${
										wallSettings.thickness === value
											? " border-accent bg-accent-soft font-semibold text-accent"
											: ""
									}`}
									onClick={() => setWallSettings({ thickness: value })}
								>
									{formatLengthNumber(value, unit)}
								</UIKit.Button>
							))}
						</div>
						<UIKit.NumberField
							label="Толщина"
							value={wallSettings.thickness}
							min={40}
							max={800}
							onChange={(thickness) => setWallSettings({ thickness })}
						/>
						<UIKit.NumberField
							label="Высота"
							value={wallSettings.height}
							min={2000}
							max={5000}
							step={50}
							onChange={(height) => setWallSettings({ height })}
						/>
					</div>

					<div className="flex flex-col gap-2">
						<UIKit.Text as="span" className={SECTION_TITLE}>
							Материал
						</UIKit.Text>
						<WallMaterialPicker
							value={wallSettings.material}
							bearing={bearing}
							onPick={(material) =>
								setWallSettings({
									material: material.material,
									thickness: material.defaultThickness,
								})
							}
						/>
					</div>

					<UIKit.Text className="rounded-lg bg-bg px-2.5 py-2 text-xs text-ink">
						Несущая стена рисуется красной. У каждого типа своя память параметров: несущая начинает
						с 250 мм и кирпича, перегородка - со 120 мм и блоков.{" "}
						<kbd className="rounded border border-border bg-surface px-1 text-xs">Tab</kbd>{" "}
						переключает тип прямо во время черчения.
					</UIKit.Text>
				</>
			)}
		</div>
	);
};
